import React, { FC, Fragment, useCallback, useState } from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  CountryFlag,
  FieldGroup,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputProps,
  Field as OptionField,
  PhoneInput,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
  isValidPhoneNumber,
} from '@column/ui';

import { AddressFields } from '~/components/AddressFields';
import { FormFieldProps } from '~/components/FormField';
import { RenderFields, SectionWithFields } from '~/components/RenderFields';
import { SearchSelect } from '~/components/SearchSelect';
import countryCodeJSON from '~/data/wires/country-codes.json';
import { useDebounce, useInstitution, useValidateIBAN } from '~/hooks';
import { useCounterpartyValidation } from '~/hooks/validate/useCounterpartyValidate';
import { FadeSwap, OVERLAP_CELL, OVERLAP_GRID, SelectedCard } from '~/lib/components/selectors';
import { COUNTERPARTY_ACCOUNT_TYPE_OPTIONS } from '~/lib/counterparties/accountType';
import { FinancialInstitution } from '~/repositories';
import { isValidIBANFormat, validateBIC, validateEmail, validateRoutingNumber } from '~/util';

import { InstitutionFlag, institutionAddress, institutionName } from './institution';
import { InstitutionSearch } from './InstitutionSearch';
import { FormData, countryName, currencyForCountry } from './types';

type Section = SectionWithFields<FormData, undefined>;

const LEGAL_TYPE_OPTIONS = [
  { label: 'Business', value: 'business' },
  { label: 'Non Profit', value: 'non_profit' },
  { label: 'Individual', value: 'individual' },
  { label: 'Sole Proprietor', value: 'sole_proprietor' },
];

const COUNTRY_OPTIONS = countryCodeJSON
  .filter(({ countryCode }) => countryCode !== 'US')
  .map(({ countryCode, countryName: name }) => ({
    label: `${name} (${countryCode})`,
    value: countryCode,
    icon: <CountryFlag country={countryCode} size={16} />,
  }));

const LOCAL_FIELDS = ['localBankCode', 'localAccountNumber'];

/** The country whose rules apply: the local bank's, else the beneficiary bank's. */
export const useRequirementsCountry = () => {
  const { watch } = useFormContext<FormData>();
  return watch('localBankCountryCode') || watch('temporaryData.institution.countryCode') || 'US';
};

type ApplyInstitutionOptions = { setRoutingNumber?: boolean };

/** Fills in what follows from the beneficiary bank: its currency, code type and country. */
export const applyInstitution = (
  { setValue, clearErrors }: Pick<UseFormReturn<FormData>, 'setValue' | 'clearErrors'>,
  institution: FinancialInstitution | null,
  { setRoutingNumber = false }: ApplyInstitutionOptions = {}
) => {
  setValue('temporaryData.institution', institution);
  setValue('temporaryData.currency', currencyForCountry(institution?.countryCode));

  if (!institution) {
    return;
  }

  clearErrors('routingNumber');
  if (institution.routingNumberType) {
    setValue('routingNumberType', institution.routingNumberType);
  }
  if (setRoutingNumber) {
    setValue('routingNumber', institution.routingNumber ?? '');
  }
  setValue('localBankCountryCode', institution.countryCode === 'US' ? '' : (institution.countryCode ?? ''));
  setValue('address.countryCode', institution.countryCode ?? '');
};

const useSetInstitution = () => {
  const { setValue, clearErrors } = useFormContext<FormData>();

  return useCallback(
    (institution: FinancialInstitution | null, options?: ApplyInstitutionOptions) =>
      applyInstitution({ setValue, clearErrors }, institution, options),
    [setValue, clearErrors]
  );
};

const SuccessInput: FC<InputProps & { isSuccess: boolean }> = ({ isSuccess, className, ...props }) => (
  <div className="relative">
    <Input {...props} className={cn('pr-10', className)} />
    <Icon.CircleCheck
      className={cn(
        'pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-seafoam-600 transition-[opacity,scale] duration-200 [--icon-size-x:16px] [--icon-size-y:16px]',
        isSuccess ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
      )}
    />
  </div>
);

const RoutingNumberInput: FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  invalid?: boolean;
}> = ({ value, onChange, onBlur, invalid }) => {
  const { watch, getValues } = useFormContext<FormData>();
  const { createRequest: fetchInstitution } = useInstitution();
  const setInstitution = useSetInstitution();
  const [status, setStatus] = useState<'idle' | 'loading' | 'not-found'>('idle');
  const institution = watch('temporaryData.institution');

  const [lookup] = useDebounce(
    async (routingNumber: string) => {
      if (!validateRoutingNumber(routingNumber) && !validateBIC(routingNumber)) {
        setStatus(routingNumber ? 'not-found' : 'idle');
        return;
      }

      const found = await fetchInstitution({ routingNumber }).catch(() => undefined);
      // A slower response for an earlier value mustn't replace the current one's.
      if (getValues('routingNumber') !== routingNumber) {
        return;
      }
      setInstitution(found ?? null);
      setStatus(found ? 'idle' : 'not-found');
    },
    400,
    []
  );

  return (
    <>
      <InputGroup className="h-10">
        <InputGroupInput
          value={value}
          onChange={(event) => {
            const next = event.target.value.trim().toUpperCase();
            onChange(next);
            if (institution && institution.routingNumber !== next) {
              setInstitution(null);
            }
            setStatus(next ? 'loading' : 'idle');
            lookup(next);
          }}
          onBlur={onBlur}
          placeholder="e.g. DEUTDEFF or 021000021"
          autoComplete="off"
          aria-invalid={invalid || status === 'not-found'}
        />
        <InputGroupAddon align="inline-end">{status === 'loading' && <Spinner />}</InputGroupAddon>
      </InputGroup>
      {status === 'not-found' && (
        <p className="mt-1.5 text-xs leading-5 text-destructive">No bank found for this BIC or routing number.</p>
      )}
    </>
  );
};

type FieldOptions = Pick<FormFieldProps<FormData>, 'rules' | 'description'>;

const useCounterpartySections = () => {
  const { setValue, watch, getValues, formState } = useFormContext<FormData>();
  const setInstitution = useSetInstitution();

  const institution = watch('temporaryData.institution');
  const iban = watch('temporaryData.iban');
  const isBIC = watch('routingNumberType') === 'bic';
  const country = useRequirementsCountry();
  const countryLabel = countryName(country) ?? country;

  const { validateField } = useCounterpartyValidation(institution?.countryCode ?? 'US');
  const { mandatoryFields, highlyRecommendedFields, validatePostalCode } = useCounterpartyValidation(country);

  const { createRequest: validateIBAN } = useValidateIBAN({
    onSuccess: (response) => {
      if (!getValues('localBankCode')) {
        setValue('localBankCode', response.bankId, { shouldValidate: true, shouldTouch: true });
      }
    },
  });

  const validateAccountNumber = useCallback(
    async (value: string) => {
      const isValid = validateField('accountNumber', value);
      if (isValid !== true || !isValidIBANFormat(value)) {
        setValue('temporaryData.iban', null);
        return isValid;
      }
      const response = await validateIBAN({ id: value }).catch(() => undefined);
      setValue('temporaryData.iban', response ?? null);
      return true;
    },
    [validateField, validateIBAN, setValue]
  );

  /** A country's requirement on a field: required, recommended, or neither. */
  const requirement = (field: string): FieldOptions => ({
    rules: { required: mandatoryFields.includes(field) },
    description: highlyRecommendedFields.includes(field) ? `Recommended for ${countryLabel}` : undefined,
  });

  const isBankFound = !!institution;
  // The sections after the bank stay closed until it's found.
  const isLocked = !isBankFound;

  const bank: Section = {
    headline: 'Beneficiary Bank',
    fields: [
      {
        id: 'temporaryData.institution',
        label: 'Bank Name',
        // Once the bank is found, it and its code collapse into the card below.
        hide: isBankFound,
        children: () => (
          <InstitutionSearch
            value={institution}
            onChange={(entry) => setInstitution(entry, { setRoutingNumber: true })}
          />
        ),
      },
      {
        id: 'routingNumber',
        label: isBankFound ? 'Bank' : 'BIC / Routing Number',
        tooltip: isBankFound
          ? undefined
          : 'BIC codes (Bank Identifier Codes) identify banks worldwide and are standard for international wires. ABA routing numbers identify banks within the United States.',
        rules: {
          required: 'Please find the beneficiary bank by name, BIC or routing number',
          validate: () => !!getValues('temporaryData.institution') || 'No bank found for this BIC or routing number',
        },
        children: ({ value, onChange, onBlur }, { error }) => (
          <div className={OVERLAP_GRID}>
            <SelectedCard
              className={OVERLAP_CELL}
              open={isBankFound}
              title={institution ? institutionName(institution) : ''}
              subtitle={countryName(institution?.countryCode) ?? institution?.countryCode}
              media={<InstitutionFlag countryCode={institution?.countryCode} size={32} />}
              rows={[
                {
                  label: institution?.routingNumberType === 'aba' ? 'Routing Number' : 'BIC',
                  value: institution?.routingNumber ?? String(value ?? ''),
                  monospaced: true,
                },
                ...(institution && institutionAddress(institution)
                  ? [{ label: 'Address', value: institutionAddress(institution)!, multiline: true }]
                  : []),
              ]}
              hasError={!!error}
              onClear={() => {
                setInstitution(null);
                onChange('');
              }}
            />
            <FadeSwap visible={!isBankFound} className={OVERLAP_CELL}>
              <RoutingNumberInput value={String(value ?? '')} onChange={onChange} onBlur={onBlur} invalid={!!error} />
            </FadeSwap>
          </div>
        ),
      },
    ],
  };

  const account: Section = {
    headline: 'Bank Account',
    isDisabled: isLocked,
    fields: [
      {
        id: 'accountNumber',
        label: isBIC ? 'Account Number / IBAN' : 'Account Number',
        // The API can still reject an IBAN that checks out here, e.g. for a different bank.
        description: iban && !formState.errors.accountNumber ? 'Valid IBAN' : undefined,
        rules: {
          required: true,
          minLength: { value: 8, message: 'Account number must be at least 8 characters' },
          validate: async (value) =>
            !isBIC || (await validateAccountNumber(String(value ?? ''))) || 'Invalid account number format',
        },
        children: ({ value, onChange, onBlur }, { error }) => (
          <SuccessInput
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={isBIC ? 'Account number or IBAN' : 'Account number'}
            aria-invalid={!!error}
            isSuccess={!error && !!value}
          />
        ),
      },
      {
        id: 'accountType',
        label: 'Account Type',
        ...requirement('accountType'),
        children: ({ value, onChange }, { error }) => (
          <RadioGroup value={value ? String(value) : ''} onValueChange={onChange}>
            <FieldGroup
              variant="outline"
              orientation="horizontal"
              className={cn(error && 'shadow-[0_0_0_1px_var(--color-destructive)] ring-[3px] ring-destructive/20')}
            >
              {COUNTERPARTY_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <OptionField key={option.value} orientation="horizontal">
                  <RadioGroupItem value={option.value} id={`intl-counterparty-account-type-${option.value}`} />
                  <FieldLabel htmlFor={`intl-counterparty-account-type-${option.value}`}>{option.label}</FieldLabel>
                </OptionField>
              ))}
            </FieldGroup>
          </RadioGroup>
        ),
      },
      {
        id: 'description',
        label: 'Description',
        rules: { required: true },
        tooltip: 'A brief description of the counterparty, shown only to you.',
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="e.g. Supplier payments"
            aria-invalid={!!error}
          />
        ),
      },
    ],
  };

  const hasLocalRequirement = LOCAL_FIELDS.some(
    (field) => mandatoryFields.includes(field) || highlyRecommendedFields.includes(field)
  );

  const local: Section = {
    headline: 'Local Bank Details',
    description: hasLocalRequirement
      ? `${countryLabel} uses domestic bank codes to route payments.`
      : 'Optional. Some countries route payments with a domestic bank code and account number.',
    // Remounted under `localKey` instead, so enabling it doesn't override `isCollapsed`.
    isDisabled: isLocked || undefined,
    isCollapsed: isLocked ? undefined : !hasLocalRequirement,
    fields: [
      {
        id: 'localBankName',
        label: 'Local Bank Name',
        tooltip: 'The official name of the beneficiary’s local bank.',
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Local bank name"
            aria-invalid={!!error}
          />
        ),
      },
      {
        id: 'localBankCode',
        label: 'Local Bank Code',
        ...requirement('localBankCode'),
        tooltip:
          'The local code of the beneficiary’s bank (e.g. India IFSC, Australia BSB, China CNAPS). Recommended for international wires and required in some countries.',
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="e.g. IFSC, BSB or CNAPS"
            aria-invalid={!!error}
          />
        ),
      },
      {
        id: 'localAccountNumber',
        label: 'Local Account Number',
        ...requirement('localAccountNumber'),
        rules: {
          ...requirement('localAccountNumber').rules,
          minLength: { value: 8, message: 'Account number must be at least 8 characters' },
        },
        children: ({ value, onChange, onBlur }, { error, isTouched }) => (
          <SuccessInput
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Local account number"
            aria-invalid={!!error}
            isSuccess={isTouched && !error && !!value}
          />
        ),
      },
      {
        id: 'localBankCountryCode',
        label: 'Local Bank Country',
        description: 'Sets which country’s requirements apply.',
        children: ({ value, onChange }, { error }) => (
          <SearchSelect
            value={String(value ?? '')}
            onChange={onChange}
            options={COUNTRY_OPTIONS}
            placeholder="Select country"
            searchPlaceholder="Search country…"
            invalid={!!error}
            pinSelected
          />
        ),
      },
    ],
  };

  const owner: Section = {
    headline: 'Account Owner',
    isDisabled: isLocked,
    fields: [
      {
        id: 'name',
        label: 'Full Name',
        rules: { required: true },
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Legal name of the person or business"
            aria-invalid={!!error}
          />
        ),
      },
      {
        id: 'email',
        label: 'Email Address',
        rules: { required: true, validate: (value) => validateEmail(String(value ?? '')) || 'Invalid email address' },
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="name@example.com"
            aria-invalid={!!error}
          />
        ),
      },
      {
        id: 'phone',
        label: 'Phone',
        ...requirement('phone'),
        rules: {
          ...requirement('phone').rules,
          validate: (value) =>
            !value || isValidPhoneNumber(String(value)) || 'Please enter a valid phone number for the selected country',
        },
        children: ({ value, onChange, onBlur }, { error }) => (
          <PhoneInput
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            valueFormat="hyphenated"
            defaultCountry={country}
            aria-invalid={!!error}
          />
        ),
      },
      {
        id: 'legalType',
        label: 'Legal Type',
        ...requirement('legalType'),
        children: ({ value, onChange }, { error }) => (
          <Select value={value ? String(value) : undefined} onValueChange={onChange}>
            <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
              <SelectValue placeholder="Select legal type" />
            </SelectTrigger>
            <SelectContent>
              {LEGAL_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: 'legalId',
        label: 'Legal ID',
        ...requirement('legalId'),
        children: ({ value, onChange, onBlur }, { error }) => (
          <Input
            value={String(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Tax or registration number"
            aria-invalid={!!error}
          />
        ),
      },
    ],
  };

  const address = (
    <AddressFields
      name="address"
      heading="Owner Address"
      required
      isDisabled={isLocked}
      fieldRules={{
        postalCode: {
          required: 'Please fill out the Postal Code field',
          validate: (value) => validatePostalCode(String(value ?? ''), country),
        },
      }}
    />
  );

  return {
    bank,
    account,
    local,
    localKey: `${country}-${isLocked}`,
    owner,
    address,
  };
};

/**
 * Every section on one page; the ones after the bank open once it's found. Local Bank Details is collapsed unless
 * the country requires or recommends a local field, so it remounts when the country changes to re-evaluate that.
 */
export const CounterpartyFields: FC = () => {
  const { bank, account, local, localKey, owner, address } = useCounterpartySections();

  return (
    <>
      {RenderFields<FormData>({ sections: [bank] })}
      <Fragment key={localKey}>{RenderFields<FormData>({ sections: [local] })}</Fragment>
      {RenderFields<FormData>({ sections: [account, owner] })}
      {address}
    </>
  );
};
